import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { get } from 'lodash';
import {
  setCommissionDeviceBeamSettings,
  GA2CommissioningSteps as Steps,
  GA2CommissioningStepStatuses as Statuses,
  doClearApplyError,
  doSetStepStatus
} from 'redux/Commissioning';
import Button from 'components/shared/Button';
import Form from 'components/shared/Form';
import FormTable from 'components/shared/FormTable';
import { ProgressIndicator } from 'components/shared/Progress';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import { useSyncFormStep } from '../../hooks';
import {
  formatOptions,
  BANDWIDTH_VALUES,
  filterAndFormatOptions,
  findArfcnByCenterFreq,
  findCenterFreqByArfcn
} from './utils';
import styles from './BeamSettingsForm.module.css';

const DEFAULT_PARAMETERS = [
  {
    name: 'centerFreq',
    label: 'ARFCN/Center Frequency',
    disabled: false,
    inputType: 'dropdown',
    unit: '(GHz)'
  },
  {
    name: 'bandwidth',
    label: 'Bandwidth',
    disabled: false,
    inputType: 'dropdown',
    unit: '(MHz)'
  },
  {
    name: 'beamTableUsed',
    label: 'Beam Table Used',
    hidden: true
  },
  {
    name: 'version',
    label: 'Beam Table Version',
    hidden: true
  }
];

function BeamSettingsForm({ value }) {
  const dispatch = useDispatch();

  const deviceData = useSelector((state) => state.data.commissioningGA2.deviceData);
  const currentStepStatus = useSelector((state) => state.data.commissioningGA2.stepStatuses[Steps.BEAM_SETTINGS]);
  const arfcnValues = useSelector((state) => state.data.commissioningGA2.arfcnValues);
  const applyError = useSelector((state) => state.data.commissioningGA2.applyError);

  const deviceProperties = deviceData?.properties?.reported;

  const methods = useForm();
  const { formState, reset, watch } = methods;
  const updatedCenterFreq = watch('centerFreq');
  const updatedBandwidth = watch('bandwidth');
  const { isDirty, isValid, isSubmitting } = formState;

  const values = useMemo(() => {
    const arfcn = get(deviceProperties, 'DU.center-arfcn', '');
    const centerFreq = findCenterFreqByArfcn(arfcn, arfcnValues);
    const beamTableUsed = get(deviceProperties, 'DU.beam-table-used', '');
    // Get beamtable info array
    const beamTableInfo = get(deviceProperties, 'DU.beamtable-info', {});
    console.log(beamTableInfo);

    let version = '';
    if (beamTableUsed && beamTableInfo) {
      const beamTableArray = Object.values(beamTableInfo);
      console.log(beamTableArray);
      const matchingEntry = beamTableArray.find(entry => {
        return entry['freq-band'] === beamTableUsed;
      });
      console.log(matchingEntry);
      version = matchingEntry?.version || '';
    }

    return {
      centerFreq,
      bandwidth: get(deviceProperties, 'DU.bandwidth', ''),
      beamTableUsed,
      version
    };
  }, [deviceProperties, arfcnValues]);
  const meta = useMemo(
    () => ({
      centerFreq: {
        lastUpdated: get(deviceProperties, '$metadata.DU.center-arfcn.$lastUpdated')
      },
      bandwidth: {
        lastUpdated: get(deviceProperties, '$metadata.DU.bandwidth.$lastUpdated')
      },
      beamTableUsed: {
        lastUpdated: get(deviceProperties, '$metadata.DU.beam-table-used.$lastUpdated')
      },
      version: {
        // Update metadata path to match the correct beamtable entry
        lastUpdated: get(deviceProperties, '$metadata.DU.beamtable-info.$lastUpdated')
      }
    }),
    [deviceProperties]
  );

  const parameters = useMemo(() => {
    const newParameters = JSON.parse(JSON.stringify(DEFAULT_PARAMETERS));

    // disable centerFreq and bandwidth dropdowns if arfcnValues failed to retrieve
    if (!arfcnValues || arfcnValues?.length === 0) {
      newParameters[0].disabled = true;
      newParameters[1].disabled = true;
    } else {
      newParameters[0].options = filterAndFormatOptions(arfcnValues);
      newParameters[1].options = formatOptions(BANDWIDTH_VALUES);
    }
    return newParameters;
  }, [arfcnValues]);

  const handleSubmit = useCallback(
    (values) => {
      const newValues = JSON.parse(JSON.stringify(values));
      const arfcn = findArfcnByCenterFreq(parseFloat(values.centerFreq), arfcnValues);
      newValues.centerFreq = arfcn;
      dispatch(setCommissionDeviceBeamSettings(newValues));
    },
    [dispatch, arfcnValues]
  );

  const handleReset = useCallback(
    (event) => {
      event.preventDefault();
      reset({ centerFreq: null, bandwidth: null });
    },
    [reset]
  );

  const handleCloseErrorBox = useCallback(() => {
    reset({ centerFreq: null, bandwidth: null });
    dispatch(doSetStepStatus(Statuses.PRISTINE, Steps.BEAM_SETTINGS));
    dispatch(doClearApplyError());
  }, [dispatch, reset]);

  useSyncFormStep(value, formState);
  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset({ centerFreq: null, bandwidth: null });
    }
  }, [formState, reset]);

  return (
    <Form methods={methods} onSubmit={handleSubmit} className={styles.root}>
      {applyError && (
        <CommissionErrorBox
          title={`${applyError.title || 'API Error'}: ${applyError.type}`}
          message={applyError.message}
          onClose={handleCloseErrorBox}
        />
      )}
      {currentStepStatus === Statuses.PENDING && <ProgressIndicator />}
      <FormTable values={values} parameters={parameters} meta={meta} />
      <div className={styles.actions}>
        <Button type="reset" variant="link" size="medium" color="primary" onClick={handleReset}>
          Clear
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="medium"
          color="primary"
          disabled={isSubmitting || !isValid || !isDirty || !updatedBandwidth || !updatedCenterFreq}
        >
          Apply
        </Button>
      </div>
    </Form>
  );
}

export default BeamSettingsForm;
